import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { tap } from 'rxjs';
import { ArticleDetailComponent } from '../../articles/article-detail.component';
import { ArticlesActions } from '../../articles/store/articles.actions';
import { ArticlesState, articlesFeature } from '../../articles/store/articles.reducer';
import { HeaderComponent, HeaderStyle, LayoutActions, LayoutState, RightPanelComponent, layoutFeature } from '../../layout';
import { getTimeAgoFromString } from '../../shared/utils';
import { UseUIBasicIconDirective } from '../../ui-components/atoms/icons/ui-basic.directive';
import { FloatingActionButtonDirective } from '../../ui-components/molecules/floating-action-button.directive';
import { ChatsHelper } from '../chats.helper';
import { ChatsSocket } from '../chats.socket';
import { ChatActions, ChatModalActions, ChatWithMessagesActions, ChatsActions } from '../store/chats.actions';
import { ChatsState, chatsFeature } from '../store/chats.reducer';
import { MessagesListingComponent } from './messages-listing';
import { PromptInputComponent } from './prompt-input';

@Component({
  selector: 'dmcom-request',
  imports: [
    HeaderComponent,
    TranslateModule,
    UseUIBasicIconDirective,
    FloatingActionButtonDirective,
    PromptInputComponent,
    MessagesListingComponent,
    NgClass,
    NgStyle,
    RightPanelComponent,
    ArticleDetailComponent,
  ],
  providers: [ChatsHelper],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="absolute top-0 left-0 w-full h-1/2 transition-opacity duration-400 ease-in-out border-4 border-white rounded-xl"
      [style.background]="'linear-gradient(85deg, var(--Primary-200, #B8A7E3) 0%, var(--Primary-100, #DCD3F1) 100%)'"
      [class.opacity-0]="!isNewConversation()"
      [class.opacity-100]="isNewConversation()"
    ></div>

    <div
      class="absolute top-0 left-0 w-full h-1/2 transition-opacity duration-400 ease-in-out"
      [style.background]="'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.88) 80%)'"
      [class.opacity-0]="!isNewConversation()"
      [class.opacity-100]="isNewConversation()"
    ></div>

    <div
      class="relative h-full w-full flex flex-col items-center mx-auto transition-all duration-400 ease-in-out"
      [class.pr-480]="isRightPanelOpened()"
      [class.pr-0]="!isRightPanelOpened() || isRightPanelMaximized()"
    >
      <dmcom-header [headerStyle]="headerStyle()"></dmcom-header>
      <dmcom-right-panel>
        <div>
          <!-- Article Detail having an extra div helps padding-->
          <dmcom-article-detail [articleData]="selectedArticle()"></dmcom-article-detail>
        </div>
      </dmcom-right-panel>

      <div
        class="relative h-full w-full flex flex-col items-center mx-auto transition-all duration-400 ease-in-out"
        [ngStyle]="{
          transform: isNewConversation() ? 'translateY(-52px)' : 'translateY(0)',
        }"
      >
        <div
          #messageContainer
          class="overflow-y-auto overflow-x-hidden py-32 w-full transition-all duration-400 ease-in-out scrollable-component"
          [ngClass]="{
            'flex-[50%_0_0]': isNewConversation(),
            'flex-1': !isNewConversation(),
          }"
        >
          <div class="flex flex-col items-center  h-full  w-full px-20 ">
            <!-- Messages List -->
            <div
              #messagesListingContainer
              class="w-full max-w-[var(--chat-container-width)]"
              [ngClass]="{
                'fade-out': isNewConversation(),
                'fade-in': !isNewConversation(),
              }"
            >
              <dmcom-messages-listing
                [messages]="selectedChatMessages()"
                [isLoading]="isLoading()"
                [isThinking]="isThinking()"
                [isMobile]="isMobile()"
                [messageCount]="selectedChat()._count?.messages || selectedChatMessages().length"
                class="flex flex-col gap-4 w-full flex-1 animate-messages-slide-up"
                (openArticleClicked)="openArticle($event)"
              ></dmcom-messages-listing>
            </div>
            <div class="flex flex-col justify-end gap-4 text-center h-full w-full">
              <div
                class="text-center typo-h1 logo-text-gradient overflow-hidden opacity: 1; height: auto;"
                [ngClass]="{
                  'fade-out': !isNewConversation(),
                }"
              >
                Compliance <span class="italic">Edge</span><span class="trademark">TM</span>
              </div>
              <div
                class="typo-p1 text-primary-800 mt-16 opacity: 1; height: auto;"
                [ngClass]="{
                  'fade-out': !isNewConversation(),
                }"
              >
                Your Essential Compliance Resource
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col items-center w-full px-20"
          [ngClass]="{
            'flex-1 gap-8': isNewConversation(),
            '': !isNewConversation(),
          }"
        >
          <!-- INPUT PROMPT -->

          <div class="flex flex-col  w-full max-w-[var(--chat-container-width)]">
            <div class="flex flex-col gap-4 w-full relative">
              <!-- SCROLL BUTTON -->
              @if (showScrollButton() && !isNewConversation()) {
                <div class="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-auto">
                  <button dmcomFloatingActionButton variant="filled" color="neutral" size="xs" (click)="scrollToBottom('smooth')">
                    <svg dmcomUseUIBasicIcon="chevron-down"></svg>
                  </button>
                </div>
              }
            </div>

            <!-- Custom error message positioned above prompt input -->
            <div class="relative w-full mb-8 flex justify-center z-[1000]" [class.hidden]="!lastMessageHasError()">
              <div class="bg-error-500 text-white px-20 py-16 rounded-lg flex items-center gap-3 shadow-sm max-w-full w-full">
                <span>{{ translate.instant('ERRORS.CHAT_MESSAGE_ERROR') }}</span>
              </div>
            </div>

            <dmcom-prompt-input #promptInput class="w-full" [isThinking]="isThinking()"></dmcom-prompt-input>
          </div>

          <!-- Promo -->
          <div
            class="promo flex flex-row gap-8  w-full max-w-[var(--chat-container-width)] bg-shades-white transition-all duration-400 ease-in-out overflow-hidden"
            [ngClass]="{
              'h-160 opacity-100': isNewConversation(),
              'h-0 opacity-0': !isNewConversation(),
            }"
          >
            <a
              (click)="router.navigate(['/articles', latestArticle()?.nodeId])"
              class="promo-1 p-20 flex flex-col gap-8 align-start w-1/2 border border-stroke-neutral rounded-2xl cursor-pointer hover:bg-grayscale-gray-lighter"
            >
              <div class="flex flex-row gap-8 justify-start items-center py-8">
                <svg dmcomUseUIBasicIcon="news-star" class="text-primary-200 w-18 h-18" size="18"></svg>
                <span class="typo-p4 text-primary-500">Latest Update</span>
              </div>
              <div class="typo-p2 text-grayscale-black font-semibold max-w-full h-50 max-h-50 overflow-hidden text-ellipsis line-clamp-2 ">
                {{ latestArticle()?.title }}
              </div>
              <div class="typo-caption text-grayscale-gray-dark mt-auto">
                {{ latestArticle() ? getTimeAgoFromString(latestArticle()!.createdAt) : '' }}
              </div>
            </a>
            <a
              href="https://www.docmagic.com"
              target="_blank"
              class="promo-2 flex flex-row w-1/2 border border-stroke-neutral
            rounded-2xl cursor-pointer hover:opacity-90 "
              style="background: url('assets/banners/docmagic-one.svg') no-repeat center center; background-size: cover;"
            >
            </a>
          </div>

          <!-- Disclaimer -->
          <div
            class="flex flex-row justify-center items-end w-full max-w-[var(--chat-container-width)]"
            [ngClass]="{
              'pt-20': !isNewConversation(),
            }"
          >
            <div
              class="absolute z-[-10] bottom-[-32px] left-0 right-0 h-24 w-full flex justify-center transition-opacity duration-400 ease-in-out"
              [class.opacity-0]="!isNewConversation()"
              [class.opacity-100]="isNewConversation()"
            >
              <div class="flex items-center justify-center gap-8 text-xs text-grayscale-gray-darker">
                <span>Powered by</span>
                <img src="/assets/logos/logo-docmagic-long.svg" class="w-123 h-24" alt="Docmagic logo" />
              </div>
            </div>

            <!-- <div
              class="absolute bottom-16 left-0 right-0 text-center text-xs text-grayscale-gray-dark w-full  transition-opacity duration-400 ease-in-out"
              [class.opacity-0]="isNewConversation()"
              [class.opacity-100]="!isNewConversation()"
            >
              Compliance Edge AI can make mistakes. Please double-check
              responses.
            </div> -->
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      ::-webkit-scrollbar {
        display: none;
      }

      .logo-text-gradient {
        letter-spacing: -0.01em;
        line-height: 120%;
        background: linear-gradient(89deg, var(--color-primary-800) 32.19%, var(--color-primary-500));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .animate-messages-slide-up {
        animation: messagesSlideUp 0.4s ease-out forwards;
        transform: translateY(40px);
        opacity: 0;
      }

      @keyframes messagesSlideUp {
        0% {
          transform: translateY(40px);
          opacity: 0;
        }
        100% {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .fade-in {
        animation: fadeIn 0.3s ease-in forwards;
      }

      .fade-out {
        animation: fadeOut 0.5s ease-out forwards;
      }

      @keyframes fadeIn {
        0% {
          opacity: 0;
          height: 0;
        }
        100% {
          opacity: 1;
          height: auto;
        }
      }

      @keyframes fadeOut {
        0% {
          opacity: 1;
          height: auto;
        }
        50% {
          opacity: 0;
          height: auto;
        }
        100% {
          opacity: 0;
          height: 0;
          display: none;
        }
      }
    `,
  ],
})
export class RequestPage implements AfterViewInit, OnInit, OnDestroy {
  private readonly articlesStore = inject<Store<ArticlesState>>(Store);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly layoutStore = inject<Store<LayoutState>>(Store);
  private readonly chatsHelper = inject(ChatsHelper);
  private readonly actions$ = inject(Actions);
  public readonly translate = inject(TranslateService);
  public readonly router = inject(Router);
  private readonly chatsSocket = inject(ChatsSocket);
  private readonly chatToDeleteId = signal<number | null>(null);

  public readonly headerStyle = computed(() => (this.isNewConversation() ? HeaderStyle.transparent : HeaderStyle.gradient));

  private readonly messageContainer = viewChild<ElementRef<HTMLDivElement>>('messageContainer');

  private readonly messagesListingContainer = viewChild<ElementRef<HTMLDivElement>>('messagesListingContainer');

  private readonly promptInput = viewChild<PromptInputComponent>('promptInput');

  public readonly isMobile = this.layoutStore.selectSignal(layoutFeature.selectIsMobile);

  public readonly isLoading = this.chatsStore.selectSignal(chatsFeature.selectIsLoading);

  public readonly isSidebarOpened = this.layoutStore.selectSignal(layoutFeature.selectIsSidebarOpened);

  public readonly isRightPanelOpened = this.layoutStore.selectSignal(layoutFeature.selectIsRightPanelOpened);

  public readonly isRightPanelMaximized = this.layoutStore.selectSignal(layoutFeature.selectIsRightPanelMaximized);

  public readonly selectedChat = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(chatsFeature.selectSelectedChat);

    return {
      ...selectedChat(),
      title: this.chatsHelper.getChatTitle(selectedChat()?.title),
    };
  });
  public readonly selectedChatMessages = computed(() => {
    const messages = this.selectedChat().messages ?? [];

    return [...messages].sort((a, b) => DateTime.fromISO(a.createdAt).toMillis() - DateTime.fromISO(b.createdAt).toMillis());
  });

  public readonly lastMessageHasError = computed(() => {
    const messages = this.selectedChatMessages();
    if (messages.length === 0) return false;

    const lastMessage = messages[messages.length - 1];
    return !!lastMessage.error && !lastMessage.errorDismissed;
  });

  public readonly isNewConversation = computed(() => !this.selectedChat()._count?.messages || this.selectedChat()._count?.messages === 0);

  public readonly selectedArticle = this.articlesStore.selectSignal(articlesFeature.selectSelectedArticle);

  private readonly selectedChatMessagesLength = signal(0);
  private readonly currentChatId = signal<number | null>(null);
  private previousChatId: number | null = null;
  public readonly isThinking = signal(false);
  public readonly showScrollButton = signal(false);

  public readonly latestArticle = this.articlesStore.selectSignal(articlesFeature.selectLatestArticle);
  // Signal to track the floating button position
  public readonly floatingButtonBottom = signal(100); // Default fallback value
  private resizeObserver?: ResizeObserver;
  private resizeObserverMessageContainer?: ResizeObserver;
  private windowResizeHandler?: () => void;

  public getTimeAgoFromString = getTimeAgoFromString;

  // Properties from ChatsPage
  public readonly selectedChatId = computed(() => {
    const selectedChat = this.chatsStore.selectSignal(chatsFeature.selectSelectedChat)();
    return selectedChat?.id ?? null;
  });
  public readonly selectedChatTitle = computed(() => {
    const title = this.chatsStore.selectSignal(chatsFeature.selectSelectedChat)()?.title;

    return this.chatsHelper.getChatTitle(title);
  });
  public readonly isDeleteChatModalOpened = this.chatsStore.selectSignal(chatsFeature.selectIsDeleteChatModalOpened);
  public readonly isNewChat = computed(() => {
    return this.selectedChat()?.messages?.length === 0;
  });

  constructor() {
    /**
     * Monitors chat messages changes to update UI state
     * (thinking state, scroll to bottom & scroll button visibility)
     * Only triggers when the same chat has new messages, not when switching chats
     */
    effect(() => {
      const currentChat = this.selectedChat();
      const messagesLength = currentChat.messages?.length ?? 0;

      // Check if we're switching to a different chat using the previousChatId variable
      const isChatChanged = currentChat.id !== this.previousChatId;
      this.previousChatId = currentChat.id ?? null;

      if (messagesLength === 0) {
        this.isThinking.set(false);
        this.focusPromptInput();

        return;
      }

      // In case we go back and forth between chats while waiting for the message to be processed,
      // if last message is from user and there are no errors, set the thinking state to true
      if (currentChat.messages?.[messagesLength - 1]?.isFromUser && !currentChat.messages?.[messagesLength - 1]?.error) {
        this.isThinking.set(true);
      } else {
        this.isThinking.set(false);
      }

      if (isChatChanged) {
        // Chat has changed, update the previous chat ID and reset message length
        this.currentChatId.set(currentChat.id ?? null);
        this.selectedChatMessagesLength.set(messagesLength);
        this.scrollToTop();
        this.focusPromptInput();

        return;
      }

      // Same chat, check if messages length has changed
      const hasMessagesLengthChanged = messagesLength !== this.selectedChatMessagesLength();

      if (hasMessagesLengthChanged) {
        this.handleNewMessages(messagesLength);
      }
    });

    /**
     * Set the thinking state when a quick action is requested
     */
    this.actions$
      .pipe(
        ofType(ChatWithMessagesActions.addChatMessage),
        takeUntilDestroyed(),
        tap(() => this.isThinking.set(true)),
      )
      .subscribe();
    /**
     * Reset the thinking state when a chat message fails to be added
     */
    this.actions$
      .pipe(
        ofType(ChatWithMessagesActions.addChatMessageFailure),
        takeUntilDestroyed(),
        tap(() => this.isThinking.set(false)),
      )
      .subscribe();
    /**
     * Reset the thinking state when a chat with messages failed
     * event is received from the socket
     */
    this.actions$
      .pipe(
        ofType(ChatWithMessagesActions.receivedChatWithMessagesFromSocketFailure),
        takeUntilDestroyed(),
        tap(() => this.isThinking.set(false)),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.articlesStore.dispatch(ArticlesActions.loadLatestArticle());
    this.chatsSocket.socket.connect();
    this.chatsSocket.socket.on('connect_error', (err) => {
      console.error(err);
    });
  }

  ngAfterViewInit() {
    const messageContainer = this.messageContainer()?.nativeElement;
    if (messageContainer) {
      messageContainer.addEventListener('scroll', () => {
        this.updateScrollButtonVisibility();
      });
    }

    // Set up height tracking for the prompt input
    this.setupPromptInputHeightTracking();

    // Focus on the prompt input when the component initializes
    this.focusPromptInput();

    const messagesListingContainer = this.messagesListingContainer()?.nativeElement;

    if (messagesListingContainer) {
      this.resizeObserverMessageContainer = new ResizeObserver(() => {
        this.updateScrollButtonVisibility();
      });
      this.resizeObserverMessageContainer.observe(messagesListingContainer);
    }

    // Add window resize listener to update scroll button visibility
    this.windowResizeHandler = () => {
      this.updateScrollButtonVisibility();
    };
    window.addEventListener('resize', this.windowResizeHandler);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeObserverMessageContainer) {
      this.resizeObserverMessageContainer.disconnect();
    }
    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler);
    }
  }

  private setupPromptInputHeightTracking(): void {
    const promptInputComponent = this.promptInput();
    if (!promptInputComponent?.container) return;

    const promptInputElement = promptInputComponent.container()?.nativeElement;
    if (!promptInputElement) return;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height;
        this.floatingButtonBottom.set(height + 120);
      }

      this.updateScrollButtonVisibility();
    });
    this.resizeObserver.observe(promptInputElement);
  }

  public scrollToBottom(behavior: 'smooth' | 'auto' = 'auto'): void {
    try {
      const messageContainer = this.messageContainer()?.nativeElement;
      if (messageContainer) {
        setTimeout(() => {
          messageContainer.scrollTo({
            top: messageContainer.scrollHeight,
            behavior,
          });
        }, 0);
      }
    } catch (err) {
      console.error('Could not scroll to bottom:', err);
    }
  }

  public scrollToTop(behavior: 'smooth' | 'auto' = 'auto'): void {
    try {
      const messageContainer = this.messageContainer()?.nativeElement;
      if (messageContainer) {
        setTimeout(() => {
          messageContainer.scrollTo({
            top: 0,
            behavior,
          });
        }, 0);
      }
    } catch (err) {
      console.error('Could not scroll to top:', err);
    }
  }

  private handleNewMessages(messagesLength: number): void {
    const isLastMessageFromChat = !this.selectedChat().messages?.[messagesLength - 1]?.isFromUser;

    if (isLastMessageFromChat) {
      this.isThinking.set(false);
    }

    this.selectedChatMessagesLength.set(messagesLength);

    setTimeout(() => {
      this.scrollToBottom();
      this.updateScrollButtonVisibility();
    }, 0);
  }

  private updateScrollButtonVisibility(): void {
    const messageContainer = this.messageContainer()?.nativeElement;
    if (messageContainer) {
      this.showScrollButton.set(Math.ceil(messageContainer.scrollTop) < messageContainer.scrollHeight - messageContainer.clientHeight);
    }
  }

  private focusPromptInput(): void {
    const promptInputComponent = this.promptInput();
    if (promptInputComponent?.textarea) {
      // Use setTimeout to ensure the view is fully rendered
      setTimeout(() => {
        const textarea = promptInputComponent.textarea()?.nativeElement;
        if (textarea) {
          textarea.focus();
        }
      }, 0);
    }
  }

  public openArticle(nodeId: number) {
    this.layoutStore.dispatch(
      LayoutActions.setRightPanelOpened({
        isOpened: true,
      }),
    );
    this.articlesStore.dispatch(ArticlesActions.loadArticleByNodeId({ nodeId }));
  }

  // Methods from ChatsPage
  public async handleNewConvoClicked() {
    this.chatsStore.dispatch(ChatActions.selectEmptyChat());
    await this.router.navigate(['/request']);
  }

  public onDeleteChat(chatId: number) {
    this.chatToDeleteId.set(chatId);
    this.chatsStore.dispatch(ChatModalActions.openDeleteChatModal());
  }

  public onTriggerAction(action: string) {
    switch (action) {
      case 'delete': {
        const chatIdToDelete = this.chatToDeleteId() ?? this.selectedChatId();
        if (chatIdToDelete !== null) {
          this.chatsStore.dispatch(ChatsActions.deleteChat({ id: chatIdToDelete }));
          this.chatToDeleteId.set(null);
        }
        this.chatsStore.dispatch(ChatModalActions.closeDeleteChatModal());
        break;
      }
      case 'cancel':
        this.chatToDeleteId.set(null);
        this.chatsStore.dispatch(ChatModalActions.closeDeleteChatModal());
        break;
    }
  }
}
